import { Group } from 'src/design/atoms/Group';
import { Paper } from 'src/design/atoms/Paper';
import { SimpleGrid } from 'src/design/atoms/SimpleGrid';
import { Stack } from 'src/design/atoms/Stack';
import { Title } from 'src/design/atoms/Title';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';
import { usePdfBlobUrl } from 'src/design/molecules/usePdfBlobUrl';
import { NumberInput } from 'src/design/atoms/NumberInput';
import { TextInput } from 'src/design/atoms/TextInput';
import { Textarea } from 'src/design/atoms/Textarea';
import { useFormik } from 'formik';
import React from 'react';
import { useJobs } from 'src/hooks/useJobs';
import * as Yup from 'yup';

import { FileDrop } from 'src/design/organisms/FileDrop/FileDrop';
import { applyAttachmentChange } from 'src/design/organisms/attachments/attachmentEdits';
import {
  IAttachedFile,
  IAttachmentChange,
} from 'src/design/organisms/attachments/attachmentTypes';
import { ChangeDocumentPicker } from 'src/routes/Jobs/Changes/components/ChangeDocumentPicker';
import { useAuth } from 'src/hooks/useAuth';

import {
  ChangeDto,
  ChangeRequestDto,
  FileResponse,
  ResourceAttachmentDto,
  WorkflowContactDto,
  WorkflowDto,
} from 'src/api/FiretrolApiClient';
import {
  NotificationCompositionRequest,
  UserDto,
  UserSearchDto,
} from 'src/api/FiretrolApiClient';
import {
  usePreviewChangePdf,
  useRotateChangeExternalAccess,
  useSaveChange,
  useUploadChangeTempFile,
} from 'src/api/queries/changes/useChangeMutations';
import {
  useUpdateAttachmentComment,
  useUploadTempAttachment,
} from 'src/api/queries/workflowModules/useAttachmentMutations';

import { useNavigate } from 'react-router-dom';
import { DatePicker } from 'src/design/atoms/DatePicker';
import { PaperStack } from 'src/design/atoms/PaperStack';
import { SubmittedBySelector } from 'src/app/selectors/SubmittedBySelector';
import { ContactPicker } from 'src/app/selectors/ContactPicker';
import { Select } from 'src/design/atoms/Select';
import { ChangeTypeSelect } from 'src/routes/Jobs/Changes/components/ChangeForm/ChangeTypeSelect';
import { useOrganization } from 'src/hooks/useOrganization';
import { IContact } from 'src/api/models/Organization';
import { useWorkflowState } from 'src/app/workflows/WorkflowButtons/useWorkflowState';
import { isStepEditable } from 'src/app/workflows/isStepEditable';
import { useNotificationComposer } from 'src/hooks/useNotificationComposer';
import { WorkflowFormFooter } from 'src/app/workflows/WorkflowFormFooter';
import { shouldOpenTransitionComposer } from 'src/app/workflows/shouldOpenTransitionComposer';
import { isTerminalStatus } from 'src/app/workflows/workflowLifecycle';
import { useWorkflowSubmission } from 'src/hooks/useWorkflowSubmission';
import {
  characterCount,
  FIELD_LIMITS,
  getErrorMessage,
  logger,
  showErrorToast,
  showToast,
} from 'src/utils';
import { RelatedFeedFooter } from 'src/routes/Jobs/ActivityFeed/components/RelatedFeedFooter';
import { useRevokeExternalLinks } from 'src/app/workflows/useRevokeExternalLinks';
import { useWorkflowFormValidation } from 'src/app/workflows/useWorkflowFormValidation';
import { useResolveAttachmentUrl } from 'src/api/queries/attachments/useAttachmentMutations';

export type {
  UserCoreDto,
  WorkflowContactDto,
} from 'src/api/FiretrolApiClient';

const SIGNED_CATEGORY = 'signed';

/** Which slice of the form to render; omit to render the full single-column form (create flow). */
export type ChangeFormSection =
  | 'details'
  | 'attachments'
  | 'signed'
  | 'notifications'
  | 'history';

export interface ChangeFormValues
  extends Omit<ChangeRequestDto, 'filesToAdd' | 'filesToRemove'> {
  attachments: ResourceAttachmentDto[];
  filesToAdd: ResourceAttachmentDto[];
  filesToRemove: ResourceAttachmentDto[];
  workflow: WorkflowDto | null;
  externalContact: IContact | null;
  changeTypeName?: string;
}

const CHANGE_FIELD_LABELS: Record<string, string> = {
  date: 'Need Date',
  description: 'Change Summary',
  impactNotes: 'Impact Notes',
  submittedByCompany: 'Submitted By Company',
};

const validationSchema = Yup.object().shape({
  // The server requires Date only when creating (no Id), so the client mirrors that.
  date: Yup.date()
    .nullable()
    .when('id', {
      is: (id?: string) => !id,
      then: (schema) => schema.required('Date is required'),
    })
    .label('Date'),
  description: Yup.string()
    .nullable()
    .max(
      FIELD_LIMITS.narrative,
      `Change Summary must be ${FIELD_LIMITS.narrative} characters or fewer`,
    ),
  impactNotes: Yup.string()
    .nullable()
    .max(
      FIELD_LIMITS.narrative,
      `Impact Notes must be ${FIELD_LIMITS.narrative} characters or fewer`,
    ),
  submittedByCompany: Yup.string()
    .nullable()
    .max(
      FIELD_LIMITS.singleLine,
      `Submitted By Company must be ${FIELD_LIMITS.singleLine} characters or fewer`,
    ),
});

export interface IChangeFormProps {
  initialValues: ChangeFormValues;
  changeId?: string;
  submittedToContact?: WorkflowContactDto;
  jobUsers?: UserDto[];
  /** In ViewChange the form is split across tabs; this selects the visible slice. Omit for the single-column create flow. */
  activeSection?: ChangeFormSection;
  /** Called after a successful save or workflow transition on an existing record. */
  onSaved?: () => void;
  /** Content for non-form tabs (notifications, history) rendered above the persistent footer. */
  children?: React.ReactNode;
}

interface IPendingTransition {
  status: string;
  stepIndex: number;
  stepLabel: string;
}

export const ChangeForm: React.FC<IChangeFormProps> = ({
  initialValues,
  changeId,
  submittedToContact,
  jobUsers = [],
  activeSection,
  onSaved,
  children,
}) => {
  const { selectedJob } = useJobs();
  const { user, isAdmin } = useAuth();
  const { offices } = useOrganization();
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnMount: true,
    // Submission is driven by WorkflowButtons via useWorkflowSubmission (submit/saveOnly/void),
    // not Formik's native submit, so this handler is intentionally a no-op.
    onSubmit: () => {},
  });

  const { setFieldValue, values } = formik;

  React.useEffect(() => {
    if (!values.id && values.assigneeUserId == null && user) {
      setFieldValue('assigneeUserId', user.id);
    }
  }, [user, values.id, values.assigneeUserId, setFieldValue]);

  const { currentStep } = useWorkflowState(
    values.workflow,
    values.stepIndex ?? -1,
  );

  const saveChange = useSaveChange();
  const previewChangePdf = usePreviewChangePdf();
  const rotateExternalAccess = useRotateChangeExternalAccess();
  const uploadSignedFile = useUploadChangeTempFile();
  const uploadTempAttachment = useUploadTempAttachment();
  const downloadAttachment = useResolveAttachmentUrl();
  const updateAttachmentComment = useUpdateAttachmentComment();

  const composer = useNotificationComposer(
    'change',
    selectedJob?.id ?? 0,
    values.id ?? '',
  );
  const [composerOpen, setComposerOpen] = React.useState(false);
  const [pendingTransition, setPendingTransition] =
    React.useState<IPendingTransition | null>(null);
  const composerMode = pendingTransition ? 'transition' : 'booking';

  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [previewFile, setPreviewFile] = React.useState<FileResponse | null>(
    null,
  );
  const [previewLoading, setPreviewLoading] = React.useState(false);
  const previewUrl = usePdfBlobUrl(previewFile?.data);

  const revoker = useRevokeExternalLinks<ChangeDto>({
    rotate: () =>
      rotateExternalAccess.mutateAsync({
        jobId: selectedJob?.id ?? 0,
        id: values.id ?? '',
      }),
    onRevoked: (dto) => {
      formik.resetForm({
        values: {
          ...values,
          attachments: dto.attachments ?? values.attachments,
        },
      });
      onSaved?.();
    },
    logLabel: 'Failed to revoke change external links',
  });

  const handlePreviewPdf = async () => {
    if (!selectedJob || !values.id) return;
    setPreviewOpen(true);
    setPreviewLoading(true);
    const file = await previewChangePdf
      .mutateAsync({ jobId: selectedJob.id, id: values.id })
      .catch(() => undefined);
    if (file === undefined) setPreviewOpen(false);
    else setPreviewFile(file);
    setPreviewLoading(false);
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewFile(null);
  };

  const isSigned = (a: ResourceAttachmentDto): boolean =>
    a.category === SIGNED_CATEGORY;
  const attachmentFiles = values.attachments.filter((a) => !isSigned(a));
  const signedFiles = values.attachments.filter(isSigned);

  const applyFileChange = (
    nextForCategory: IAttachedFile[],
    signedSlice: boolean,
    change: IAttachmentChange,
  ) => {
    formik.setValues((v) => {
      const edits = applyAttachmentChange(
        { added: v.filesToAdd, removed: v.filesToRemove },
        change,
      );
      const others = v.attachments.filter((a) =>
        signedSlice ? !isSigned(a) : isSigned(a),
      );
      return {
        ...v,
        attachments: [
          ...(nextForCategory as ResourceAttachmentDto[]),
          ...others,
        ],
        filesToAdd: edits.added as ResourceAttachmentDto[],
        filesToRemove: edits.removed as ResourceAttachmentDto[],
      };
    });
  };

  const handleCommentSave = async (file: IAttachedFile, comment: string) => {
    const updated = await updateAttachmentComment
      .mutateAsync({
        jobId: selectedJob?.id ?? 0,
        attachmentId: file.id,
        comment,
      })
      .catch(() => null);
    if (!updated) return;
    formik.setValues((v) => ({
      ...v,
      attachments: v.attachments.map((a) =>
        a.id === file.id ? { ...a, comment: updated.comment ?? comment } : a,
      ),
    }));
  };

  const handleDocumentAttached = (attachment: ResourceAttachmentDto) => {
    formik.setValues((v) => ({
      ...v,
      attachments: [...v.attachments, attachment],
      filesToAdd: [...v.filesToAdd, attachment],
    }));
  };

  const buildRequest = (
    overrides: Partial<ChangeRequestDto> = {},
  ): ChangeRequestDto => ({
    id: changeId || undefined,
    notification: undefined,
    date: values.date,
    description: values.description,
    status: values.status,
    impactNotes: values.impactNotes,
    assigneeUserId: values.assigneeUserId,
    submittedByCompany: values.submittedByCompany,
    changeTypeId: values.changeTypeId,
    stepIndex: values.stepIndex,
    workflowId: values.workflowId,
    externalContact: values.externalContact,
    submittedToContactId: values.submittedToContactId,
    proposedAmount: values.proposedAmount,
    proposedDays: values.proposedDays,
    approvedAmount: values.approvedAmount,
    approvedDays: values.approvedDays,
    filesToAdd: values.filesToAdd?.map((f) => ({ id: f.id })),
    filesToRemove: values.filesToRemove?.map((f) => ({ id: f.id })),
    saveOnly: false,
    ...overrides,
  });

  const {
    submitWorkflow,
    voidWorkflow,
    saveOnly,
    error,
    fieldErrors,
    isSubmitting,
  } = useWorkflowSubmission<ChangeFormValues, ChangeRequestDto>(
    values,
    selectedJob?.id ?? 0,
    {
      mutations: { save: saveChange },
      toRequest: (_vals, overrides) =>
        buildRequest({
          status: overrides?.status,
          stepIndex: overrides?.stepIndex,
          saveOnly: overrides?.saveOnly ?? false,
          notification: overrides?.notification,
        }),
      entityName: 'Change',
      toast: {
        getEntityNumber: (c) => (c as unknown as ChangeDto).changeNumber ?? '',
      },
    },
    {
      onCreated: (result) => {
        const dto = result as unknown as ChangeDto;
        if (selectedJob) {
          navigate(`/jobs/${selectedJob.id}/changes/view/${dto.id}`);
        }
      },
      onSaved: () => onSaved?.(),
      setValues: (result) => {
        const dto = result as unknown as ChangeDto;
        // resetForm re-baselines initialValues so formik.dirty clears once the
        // server state is persisted.
        formik.resetForm({
          values: {
            ...values,
            ...dto,
            attachments: dto.attachments ?? values.attachments,
            filesToAdd: [],
            filesToRemove: [],
            workflow: dto.workflow ?? values.workflow,
            // Preserve parsed externalContact from form state (DTO stores it serialised)
            externalContact: values.externalContact,
          },
        });
      },
    },
  );

  const { passesValidation } = useWorkflowFormValidation(
    formik,
    fieldErrors,
    CHANGE_FIELD_LABELS,
  );

  const handleSaveOnly = async () => {
    if (!(await passesValidation())) return;
    await saveOnly();
  };

  const openComposerWithPreview = async (
    eventType: string,
    onFailure?: () => void,
  ): Promise<boolean> => {
    setComposerOpen(true);
    const preview = await composer.loadPreview(eventType);
    if (!preview) {
      setComposerOpen(false);
      onFailure?.();
      showErrorToast(
        'Unable to load the notification preview. Please try again.',
      );
      return false;
    }
    return true;
  };

  const onSubmitClick = async (status: string, nextStepIndex: number) => {
    if (!(await passesValidation())) return;

    const currentStepIndex = values.stepIndex ?? -1;
    const isForward = nextStepIndex === currentStepIndex + 1;
    const targetStep = values.workflow?.steps?.[nextStepIndex] ?? null;

    if (
      shouldOpenTransitionComposer({
        isForward,
        hasId: !!values.id,
        targetStep,
        excludeLastStep: true,
      })
    ) {
      setPendingTransition({
        status,
        stepIndex: nextStepIndex,
        stepLabel: targetStep.label ?? '',
      });
      await openComposerWithPreview(`StepAdvanced:${nextStepIndex}`, () =>
        setPendingTransition(null),
      );
      return;
    }

    const result = await submitWorkflow(status, nextStepIndex);

    if (result && isForward && values.id && targetStep?.isLastStep) {
      await openComposerWithPreview('Booking');
    }
  };

  const submitTransition = async (request: NotificationCompositionRequest) => {
    if (!pendingTransition) return;
    try {
      const result = await submitWorkflow(
        pendingTransition.status,
        pendingTransition.stepIndex,
        false,
        { notification: request },
      );
      if (!result) {
        showErrorToast('Unable to submit the change. Please try again.');
        return;
      }
      setComposerOpen(false);
      setPendingTransition(null);
    } catch (err: unknown) {
      logger.error('Failed to submit change transition', { err });
      showErrorToast(getErrorMessage(err));
    }
  };

  const cancelTransition = () => {
    setComposerOpen(false);
    setPendingTransition(null);
    showToast('Submission canceled — status unchanged');
  };

  const sendBooking = async (request: NotificationCompositionRequest) => {
    try {
      await composer.send(request);
      setComposerOpen(false);
    } catch (err: unknown) {
      logger.error('Failed to send booking notification', { err });
      showErrorToast(getErrorMessage(err));
    }
  };

  const skipBooking = async (request: NotificationCompositionRequest) => {
    try {
      await composer.skip(request);
      setComposerOpen(false);
    } catch (err: unknown) {
      logger.error('Failed to skip booking notification', { err });
      showErrorToast(getErrorMessage(err));
    }
  };

  const cancelBooking = () =>
    skipBooking({
      eventType: composer.preview?.eventType ?? 'Booking',
      to: [],
      cc: [],
      bcc: [],
      attachPdf: false,
      skipEmail: true,
    });

  const submittedByInitial = React.useMemo<UserSearchDto | undefined>(() => {
    if (
      !user ||
      values.assigneeUserId == null ||
      values.assigneeUserId !== user.id
    ) {
      return undefined;
    }
    return {
      id: user.id,
      displayName: user.fullName || `${user.firstName} ${user.lastName}`,
      primaryOfficeName: undefined,
    };
  }, [
    user?.id,
    user?.fullName,
    user?.firstName,
    user?.lastName,
    values.assigneeUserId,
  ]);

  if (!selectedJob || !user) return null;

  const customerNumber = selectedJob.customerNumber?.trim() ?? '';
  const submittedToInitial: IContact | null = values.submittedToContactId
    ? submittedToContact &&
      submittedToContact.id === values.submittedToContactId
      ? {
          id: submittedToContact.id,
          first_name: submittedToContact.name ?? '',
          last_name: '',
          phone: '',
          email: submittedToContact.email ?? '',
          customer_number: '',
          customer_name: submittedToContact.company ?? '',
        }
      : {
          id: values.submittedToContactId,
          first_name: '',
          last_name: '',
          phone: '',
          email: '',
          customer_number: '',
          customer_name: '',
        }
    : null;
  const showApprovedFields = !!(
    currentStep?.isExternalStep || currentStep?.isLastStep
  );
  const stepEditable = isStepEditable({
    isAdmin,
    currentStep,
    status: values.status,
  });
  const approvedFieldsEditable =
    !!currentStep?.isExternalStep || (isAdmin && stepEditable);

  const companyData = offices.map((o) => ({ value: o.name, label: o.name }));
  const changeTypeValue = values.changeTypeId
    ? { id: values.changeTypeId, name: values.changeTypeName ?? '' }
    : null;

  const showDetails = !activeSection || activeSection === 'details';
  const showAttachments = !activeSection || activeSection === 'attachments';
  const showSigned = activeSection === 'signed';

  const composerConfig =
    composerMode === 'transition'
      ? {
          onClose: cancelTransition,
          submitting: isSubmitting,
          title: `${pendingTransition?.stepLabel ?? ''} — notification`,
          toPlaceholder: undefined,
          onSend: submitTransition,
          onSkip: submitTransition,
        }
      : {
          onClose: cancelBooking,
          submitting: composer.submitting,
          title: 'Booking email',
          toPlaceholder: 'Add the booking/accounting recipient',
          onSend: sendBooking,
          onSkip: skipBooking,
        };

  return (
    <Stack>
      {showDetails && (
        <>
          <PaperStack>
            <Group justify="space-between">
              <Title order={5}>{`Status: ${values.status}`}</Title>
              {values.id && (
                <SecondaryButton onClick={handlePreviewPdf}>
                  Preview PDF
                </SecondaryButton>
              )}
            </Group>

            <Textarea
              label="Change Summary"
              disabled={!stepEditable}
              onChange={(e) => setFieldValue('description', e.target.value)}
              onBlur={formik.handleBlur}
              value={values.description || ''}
              id="desc"
              name="description"
              autosize
              minRows={3}
              maxLength={FIELD_LIMITS.narrative}
              description={characterCount(
                values.description,
                FIELD_LIMITS.narrative,
              )}
              inputWrapperOrder={['label', 'input', 'description', 'error']}
              error={formik.touched.description && formik.errors.description}
            />
          </PaperStack>
          <Paper>
            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              <SubmittedBySelector
                label="Submitted By"
                disabled={!stepEditable}
                value={values.assigneeUserId ?? undefined}
                initialUser={submittedByInitial}
                onChange={(picked) => {
                  setFieldValue('assigneeUserId', picked?.id ?? undefined);
                  if (picked?.primaryOfficeName) {
                    setFieldValue(
                      'submittedByCompany',
                      picked.primaryOfficeName,
                    );
                  }
                }}
              />
              <Select
                label="Submitted By Company"
                disabled={!stepEditable}
                placeholder="Select company..."
                data={companyData}
                value={values.submittedByCompany || null}
                onChange={(val) =>
                  setFieldValue('submittedByCompany', val ?? '')
                }
                error={
                  formik.touched.submittedByCompany &&
                  formik.errors.submittedByCompany
                }
                searchable
                clearable
                clearButtonProps={{ 'aria-label': 'Clear company' }}
              />
              <ChangeTypeSelect
                disabled={!stepEditable}
                value={changeTypeValue}
                onChange={(type) => {
                  setFieldValue('changeTypeId', type?.id ?? undefined);
                  setFieldValue('changeTypeName', type?.name ?? undefined);
                }}
              />
              <DatePicker
                label="Need Date"
                disabled={!stepEditable}
                value={values.date}
                onChange={(value) => {
                  setFieldValue('date', value ?? null);
                }}
                name="date"
                withAsterisk
                error={
                  formik.touched.date &&
                  (formik.errors.date as string | undefined)
                }
              />
              <Textarea
                label="Impact Notes"
                disabled={!stepEditable}
                onChange={(e) => setFieldValue('impactNotes', e.target.value)}
                onBlur={formik.handleBlur}
                value={values.impactNotes || ''}
                id="impact"
                name="impactNotes"
                autosize
                minRows={4}
                maxLength={FIELD_LIMITS.narrative}
                description={characterCount(
                  values.impactNotes,
                  FIELD_LIMITS.narrative,
                )}
                inputWrapperOrder={['label', 'input', 'description', 'error']}
                error={formik.touched.impactNotes && formik.errors.impactNotes}
              />
              <ContactPicker
                label="Submitted To"
                disabled={!stepEditable}
                allowCreate
                customerNumber={customerNumber}
                customerName={selectedJob.customerName ?? ''}
                initialValue={submittedToInitial}
                onChange={(contact) => {
                  setFieldValue(
                    'submittedToContactId',
                    contact?.id ?? undefined,
                  );
                }}
              />
              <Stack>
                <NumberInput
                  label="Proposed Amount"
                  disabled={!stepEditable}
                  id="change-proposed-amount-input"
                  placeholder="$0"
                  prefix="$"
                  value={values.proposedAmount ?? ''}
                  decimalScale={2}
                  thousandSeparator=","
                  styles={{ input: { textAlign: 'right' } }}
                  onChange={(value) => {
                    setFieldValue(
                      'proposedAmount',
                      typeof value === 'number' ? value : undefined,
                    );
                  }}
                />
                <NumberInput
                  label="Proposed Days"
                  disabled={!stepEditable}
                  id="change-proposed-days-input"
                  placeholder="0"
                  value={values.proposedDays ?? ''}
                  decimalScale={0}
                  styles={{ input: { textAlign: 'right' } }}
                  onChange={(value) => {
                    setFieldValue(
                      'proposedDays',
                      typeof value === 'number' ? value : undefined,
                    );
                  }}
                />
              </Stack>
              {showApprovedFields && (
                <Stack>
                  <NumberInput
                    label="Approved Amount"
                    id="change-approved-amount-input"
                    placeholder="$0"
                    prefix="$"
                    value={values.approvedAmount ?? ''}
                    readOnly={!approvedFieldsEditable}
                    decimalScale={2}
                    thousandSeparator=","
                    styles={{ input: { textAlign: 'right' } }}
                    onChange={(value) => {
                      setFieldValue(
                        'approvedAmount',
                        typeof value === 'number' ? value : undefined,
                      );
                    }}
                  />
                  <NumberInput
                    label="Approved Days"
                    id="change-approved-days-input"
                    placeholder="0"
                    value={values.approvedDays ?? ''}
                    readOnly={!approvedFieldsEditable}
                    decimalScale={0}
                    styles={{ input: { textAlign: 'right' } }}
                    onChange={(value) => {
                      setFieldValue(
                        'approvedDays',
                        typeof value === 'number' ? value : undefined,
                      );
                    }}
                  />
                </Stack>
              )}
            </SimpleGrid>
          </Paper>
        </>
      )}
      {showAttachments && (
        <PaperStack>
          <Group justify="space-between">
            <Title order={5}>Attachments</Title>
            {values.id && attachmentFiles.some((f) => f.externalUrl) && (
              <SecondaryButton onClick={revoker.openModal}>
                Revoke links
              </SecondaryButton>
            )}
          </Group>

          <ChangeDocumentPicker
            jobId={selectedJob.id}
            onAttached={handleDocumentAttached}
          />

          <FileDrop
            files={attachmentFiles}
            onChange={(files, change) => applyFileChange(files, false, change)}
            onCommentSave={handleCommentSave}
            onCopyLink={revoker.copyLink}
            uploadFile={(file) =>
              uploadTempAttachment.mutateAsync({
                typeId: 'change_request',
                file,
              })
            }
            downloadFile={(id) => downloadAttachment.mutateAsync(id)}
          />
        </PaperStack>
      )}
      {showSigned && (
        <PaperStack>
          <Title order={5}>Signed Documents</Title>

          <FileDrop
            files={signedFiles}
            onChange={(files, change) =>
              applyFileChange(
                files.map((f) =>
                  f.category === SIGNED_CATEGORY
                    ? f
                    : { ...f, category: SIGNED_CATEGORY },
                ),
                true,
                change,
              )
            }
            onCommentSave={handleCommentSave}
            uploadFile={(file) =>
              uploadSignedFile.mutateAsync({
                file,
                category: SIGNED_CATEGORY,
              })
            }
            downloadFile={(id) => downloadAttachment.mutateAsync(id)}
          />
        </PaperStack>
      )}
      {showDetails && (
        <>
          {values.externalContact && (
            <PaperStack>
              <Title order={5}>Client Contact</Title>

              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <TextInput
                  label="Name"
                  id="name"
                  value={`${values.externalContact.first_name} ${values.externalContact.last_name}`}
                  disabled
                />
                <TextInput
                  label="Email"
                  id="email"
                  name="email"
                  value={values.externalContact.email}
                  disabled
                />
              </SimpleGrid>
            </PaperStack>
          )}
          <RelatedFeedFooter
            jobId={selectedJob.id}
            filter={{ changeId }}
            enabled={!!changeId}
          />
        </>
      )}

      {children}

      <WorkflowFormFooter
        dirty={formik.dirty}
        showButtons={!isTerminalStatus(values.status)}
        error={error}
        onSubmitClick={onSubmitClick}
        workflow={values.workflow}
        onSaveOnlyClick={handleSaveOnly}
        onVoidClick={voidWorkflow}
        stepIndex={values.stepIndex ?? -1}
        composerOpen={composerOpen}
        onComposerClose={composerConfig.onClose}
        preview={composer.preview}
        jobUsers={jobUsers}
        customerNumber={customerNumber}
        composerSubmitting={composerConfig.submitting}
        composerTitle={composerConfig.title}
        composerToPlaceholder={composerConfig.toPlaceholder}
        attachPdfLabel="Attach change PDF"
        onSend={composerConfig.onSend}
        onSkip={composerConfig.onSkip}
        onReloadPreview={composer.reloadPreview}
        previewOpen={previewOpen}
        onPreviewClose={closePreview}
        pdfTitle="Change Order PDF"
        previewUrl={previewUrl}
        previewLoading={previewLoading}
        revokeOpen={revoker.modalOpen}
        onRevokeClose={revoker.closeModal}
        onRevokeConfirm={revoker.revoke}
        revoking={revoker.revoking}
        revokeEntityNoun="change"
        revokeItemsNoun="attachments"
      />
    </Stack>
  );
};
